import { advancedThresholdValuePropertyName } from "@/cache/CustomGeoJSONCache/utils";
import { parseHexWithAlpha } from "@/layers/utility/color";
import type { AdvancedThreshold } from "@/overlay/components/CommonParts/Threshold/types";
import type { Expression } from "mapbox-gl";

/**
 * Converts an array of advanced color thresholds into a Mapbox GL expression or a default color string.
 *
 * @param {string} defaultColor - The default color to use if no thresholds match or if any value is null.
 * @param {AdvancedThreshold[]} [thresholds] - An optional array of advanced color thresholds.
 *   - Each `AdvancedThreshold` must have:
 *     - `label`: A descriptive label for the threshold.
 *     - `color`: A hex color string (with or without alpha).
 *     - `conditions`: An array containing a single logical operator `"and"` or `"or"`,
 *                     which is converted to `"all"` or `"any"` in Mapbox expressions.
 *     - `details`: An array of conditions, where each condition must have:
 *       - `id`: A unique identifier used to retrieve the corresponding feature property.
 *       - `parameter`: The name of the parameter being evaluated.
 *       - `value`: The numeric threshold value for comparison.
 *       - `condition`: A comparison operator (e.g., `"<="`, `">="`).
 *       - `offsetMinutes`: A time offset in minutes.
 *
 * @returns {Expression | string} A Mapbox GL `Expression` if thresholds are valid,
 *                                or the `defaultColor` string otherwise.
 *
 * @example
 * const thresholds = [
 *   {
 *     label: "Cold",
 *     color: "#0000FF",
 *     conditions: ["and"], // Will be converted to "all"
 *     details: [
 *       { id: 1, parameter: "temperature", value: 10, condition: "<=", offsetMinutes: 0 },
 *       { id: 2, parameter: "humidity", value: 50, condition: ">=", offsetMinutes: 0 },
 *     ],
 *   },
 *   {
 *     label: "Rainy",
 *     color: "#00FFFF",
 *     conditions: ["or"], // Will be converted to "any"
 *     details: [
 *       { id: 3, parameter: "precipitation", value: 20, condition: ">=", offsetMinutes: 0 },
 *       { id: 4, parameter: "cloud_coverage", value: 70, condition: ">=", offsetMinutes: 0 },
 *     ],
 *   },
 * ];
 *
 * const defaultColor = "#000000";
 * const expression = advancedThresholdToMapboxExpression(defaultColor, thresholds);
 * // Returns:
 * // [
 * //   "case",
 * //   ["any",
 * //     ["==", ["get", "value1"], null],
 * //     ["==", ["get", "value2"], null],
 * //     ["==", ["get", "value3"], null],
 * //     ["==", ["get", "value4"], null]
 * //   ],
 * //   "#000000", // Default color if any value{id} is null
 * //
 * //   ["all",
 * //     ["<=", ["get", "value1"], 10],
 * //     [">=", ["get", "value2"], 50]
 * //   ],
 * //   "#0000FF",
 * //
 * //   ["any",
 * //     [">=", ["get", "value3"], 20],
 * //     [">=", ["get", "value4"], 70]
 * //   ],
 * //   "#00FFFF",
 * //
 * //   "#000000"
 * // ]
 */
export const advancedThresholdColorExpression = (
  defaultColor: string,
  thresholds?: AdvancedThreshold[],
  valueExpressionForProperty: (propertyName: string) => Expression = (propertyName) => ["get", propertyName],
): Expression | string => {
  if (!thresholds || thresholds.length === 0) {
    return parseHexWithAlpha(defaultColor);
  }

  const caseExpression: Expression = ["case"];

  // Ensure that at least one "value{id}" is present and not null
  const valueNullCheck: Expression = ["any"]; // Use "any" to check multiple fields

  for (const threshold of thresholds) {
    for (const detail of threshold.details) {
      // check model and parameter are there
      if (detail.model && detail.parameter) {
        valueNullCheck.push(["==", valueExpressionForProperty(advancedThresholdValuePropertyName(detail.id)), null]);
      }
    }
  }

  // Add null check as the first case
  caseExpression.push(valueNullCheck.length > 1 ? valueNullCheck : valueNullCheck[1]);
  caseExpression.push(parseHexWithAlpha(defaultColor));

  // Process threshold conditions
  for (const threshold of thresholds) {
    const conditions: Expression[] = threshold.details
      .filter((detail) => detail.model && detail.parameter)
      .map(
        (detail) =>
          [
            detail.condition,
            valueExpressionForProperty(advancedThresholdValuePropertyName(detail.id)),
            detail.value,
          ] as Expression,
      );

    const combinedCondition: Expression =
      conditions.length > 1 ? [threshold.conditions[0] === "or" ? "any" : "all", ...conditions] : conditions[0];

    caseExpression.push(combinedCondition, parseHexWithAlpha(threshold.color));
  }

  // Default color if no conditions match
  caseExpression.push(parseHexWithAlpha(defaultColor));
  return caseExpression;
};
